export const SERVICE_POINT_PROFILES = ['AGENDAMENTO', 'TRIAGEM', 'GUICHE', 'SUPERVISOR'] as const;

export type ServicePointProfile = (typeof SERVICE_POINT_PROFILES)[number];

export const MEMBERS_SEARCH_MIN_LENGTH = 2;

export const MEMBERS_SEARCH_MAX_LENGTH = 100;

export const MEMBERS_SEARCH_LIMIT = 50;

export type MembershipStatus = 'NONE' | 'ACTIVE' | 'SCHEDULED' | 'ENDED';

export interface MemberView {
  user_id: string;
  service_point_id: string;
  profiles: ServicePointProfile[];
  valid_from: string;
  valid_until: string | null;
  active: boolean;
}

export interface MemberListItem extends MemberView {
  display_name: string | null;
  primary_email: string | null;
  primary_role: string | null;
}

export interface CandidateItem {
  user_id: string;
  display_name: string | null;
  primary_email: string | null;
  primary_role: string | null;
  membership: MemberView | null;
}

export interface ServicePointOption {
  id: string;
  name: string;
}

export interface OperablePoint extends ServicePointOption {
  profiles: ServicePointProfile[];
}

export interface ListMembersResponse {
  service_point: { id: string; active: boolean };
  members: MemberListItem[];
  candidates: CandidateItem[];
}

export interface CaseRef {
  case_id: string;
  summary: string | null;
}

// One agent's membership at a point, as the agent "Tools" tab reads it (CPK-5087).
export interface UserMembership extends MemberView {
  service_point: { id: string; name: string; address: string | null; active: boolean };
  // Cases of the point's open campaigns where the agent has no valid `view`.
  cases_without_view: CaseRef[];
}

export interface ListUserMembershipsResponse {
  memberships: UserMembership[];
}

export interface UpsertMemberInput {
  service_point_id: string;
  user_id: string;
  profiles: ServicePointProfile[];
  valid_from?: string;
  valid_until?: string | null;
}

export interface UpsertMemberResponse {
  member: MemberView;
}

export interface RemoveMemberResponse {
  member: MemberView | null;
  changed: boolean;
}

export type MembershipDates = Pick<MemberView, 'valid_from' | 'valid_until'>;

export function membershipStatus(member: MembershipDates | null, now: number = Date.now()): MembershipStatus {
  if (!member) return 'NONE';
  if (member.valid_until !== null && new Date(member.valid_until).getTime() <= now) return 'ENDED';
  if (new Date(member.valid_from).getTime() > now) return 'SCHEDULED';
  return 'ACTIVE';
}

export function sortProfiles(profiles: readonly ServicePointProfile[]): ServicePointProfile[] {
  return SERVICE_POINT_PROFILES.filter((profile) => profiles.includes(profile));
}

export function effectiveProfiles(member: MemberView | null, now: number = Date.now()): ServicePointProfile[] {
  const status = membershipStatus(member, now);
  if (!member || status === 'ENDED') return [];
  return sortProfiles(member.profiles);
}

export function holdsAnyProfile(
  held: readonly string[],
  required: readonly ServicePointProfile[]
): boolean {
  return held.includes('SUPERVISOR') || held.some((profile) => required.includes(profile as ServicePointProfile));
}
